<template>
    <div class="pt-4">
        <ULink to="/telemedicine/doctors" class="inline-flex items-center space-x-1 text-sm font-semibold text-gray-500 hover:text-gray-700">
            <span>Doctors</span>
            <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
        </ULink>

        <div v-if="loading" class="mt-3 space-y-6">
            <div class="flex items-center gap-4">
                <USkeleton class="size-16 rounded-lg" />
                <div class="space-y-2">
                    <USkeleton class="h-7 w-64" />
                    <USkeleton class="h-4 w-48" />
                </div>
            </div>
            <div class="grid gap-4 lg:grid-cols-3">
                <USkeleton class="h-96 lg:col-span-2" />
                <USkeleton class="h-64" />
            </div>
        </div>

        <div v-else-if="!profile" class="mt-6 flex flex-col items-center rounded-lg bg-white px-6 py-16 text-center shadow-sm ring-1 ring-black/5">
            <UIcon name="i-ph-stethoscope-duotone" class="size-8 text-gray-400" />
            <h2 class="mt-3 text-base font-semibold text-gray-800">Doctor not found</h2>
            <p class="mt-1 text-sm text-gray-500">It may have been removed, or the link is incorrect.</p>
            <UButton to="/telemedicine/doctors" color="white" class="mt-5" icon="i-ph-arrow-left-bold">Back to doctors</UButton>
        </div>

        <template v-else>
            <div class="mt-2 mb-6 flex flex-wrap items-center justify-between gap-4">
                <div class="flex min-w-0 items-center gap-4">
                    <UAvatar :src="photoUrl" :alt="profile.full_name" size="2xl" :ui="{ rounded: 'rounded-lg' }" />
                    <div class="min-w-0 space-y-1">
                        <div class="flex items-center gap-3">
                            <h1 class="truncate text-2xl font-semibold text-gray-700">{{ displayName }}</h1>
                            <TelemedicineDoctorStatusBadge :status="profile.status" />
                        </div>
                        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                            <span class="flex items-center gap-1.5">
                                <UIcon name="i-ph-envelope-simple-duotone" class="size-4 text-gray-400" />
                                {{ profile.email }}
                            </span>
                            <span v-if="profile.specialty" class="flex items-center gap-1.5">
                                <UIcon name="i-ph-first-aid-kit-duotone" class="size-4 text-gray-400" />
                                {{ profile.specialty.name_en }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="flex items-center gap-2">
                    <UButton :to="`/telemedicine/doctors/${profile.id}/schedule`" color="white" icon="i-ph-calendar-dots-duotone">
                        Schedule
                    </UButton>
                    <UButton @click="resend()" :loading="busy === 'resend'" color="white" icon="i-ph-envelope-simple-duotone">
                        Resend access email
                    </UButton>
                    <UButton
                        v-if="canSuspend"
                        @click="overlays.suspend = true"
                        color="white"
                        icon="i-ph-prohibit-duotone"
                        :ui="{ icon: { base: 'text-red-500' } }"
                    >
                        Suspend
                    </UButton>
                    <UButton
                        v-if="profile.status === DoctorStatus.published"
                        @click="setStatus(DoctorStatus.draft)"
                        :loading="busy === DoctorStatus.draft"
                        color="black"
                        icon="i-ph-eye-slash-duotone"
                    >
                        Unpublish
                    </UButton>
                    <UButton
                        v-else-if="profile.status === DoctorStatus.suspended"
                        @click="setStatus(DoctorStatus.draft)"
                        :loading="busy === DoctorStatus.draft"
                        color="black"
                        icon="i-ph-arrow-counter-clockwise-duotone"
                    >
                        Reinstate to draft
                    </UButton>
                    <UTooltip
                        v-else
                        :prevent="publishReady"
                        :popper="{ placement: 'bottom-end' }"
                        :ui="{ base: '[@media(pointer:coarse)]:hidden h-auto px-3 py-2 text-xs font-normal relative' }"
                    >
                        <template #text>
                            <div class="space-y-1 py-0.5">
                                <p class="font-medium">{{ dirty ? 'Save your changes first' : 'Before publishing:' }}</p>
                                <ul v-if="!dirty" class="space-y-0.5">
                                    <li v-for="blocker in liveBlockers" :key="blocker">• {{ blocker }}</li>
                                </ul>
                            </div>
                        </template>
                        <span class="inline-flex">
                            <UButton
                                @click="setStatus(DoctorStatus.published)"
                                :loading="busy === DoctorStatus.published"
                                :disabled="!publishReady"
                                :class="{ 'pointer-events-none': !publishReady }"
                                color="green"
                                icon="i-ph-broadcast-duotone"
                            >
                                Publish
                            </UButton>
                        </span>
                    </UTooltip>
                </div>
            </div>

            <UAlert
                v-if="profile.status === DoctorStatus.suspended"
                color="red"
                variant="subtle"
                icon="i-ph-prohibit-duotone"
                title="This doctor is suspended"
                description="They are hidden from the app and cannot receive new bookings. Reinstate them to draft to make changes and publish again."
                class="mb-4"
            />

            <div class="grid items-start gap-4 lg:grid-cols-3">
                <div class="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5 lg:col-span-2">
                    <UForm v-if="state" ref="form" @submit="save" :schema="doctorProfileFormSchema" :state="state" :validate-on="['submit']">
                        <div class="flex items-center justify-between border-b border-gray-300 px-6 py-3">
                            <h3 class="text-lg font-bold text-gray-700">Profile</h3>
                            <span v-if="dirty" class="flex items-center gap-1.5 text-xs font-medium text-amber-600">
                                <span class="size-1.5 rounded-full bg-amber-500" />
                                Unsaved changes
                            </span>
                        </div>
                        <div class="px-6 py-5">
                            <TelemedicineDoctorProfileForm v-model="state" mode="admin" :specialties="specialties" :specialties-unavailable="specialtiesFailed" :profile-id="profile.id" />
                        </div>
                        <div class="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50/60 px-6 py-3">
                            <UButton v-if="dirty" @click="reset()" color="white">Discard</UButton>
                            <UButton type="submit" :loading="saving" :disabled="!dirty" color="black" icon="i-ph-floppy-disk-duotone">
                                Save changes
                            </UButton>
                        </div>
                    </UForm>
                </div>

                <div class="space-y-4">
                    <div class="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                        <div class="flex items-center justify-between border-b border-gray-300 px-6 py-3">
                            <h3 class="text-lg font-bold text-gray-700">Publishing checklist</h3>
                            <span class="text-sm font-medium tabular-nums" :class="checklistDone === checklist.length ? 'text-green-600' : 'text-gray-500'">
                                {{ checklistDone }}/{{ checklist.length }}
                            </span>
                        </div>
                        <ul class="space-y-3 px-6 py-5">
                            <li v-for="item in checklist" :key="item.key" class="flex items-center gap-2.5 text-sm">
                                <UIcon
                                    :name="item.ok ? 'i-ph-check-circle-fill' : 'i-ph-x-circle-fill'"
                                    class="size-5 flex-shrink-0"
                                    :class="item.ok ? 'text-green-500' : 'text-red-400'"
                                />
                                <span :class="item.ok ? 'text-gray-700' : 'text-gray-500'">{{ item.label }}</span>
                            </li>
                        </ul>
                        <div class="border-t border-dashed border-gray-300 px-6 py-3 text-sm" :class="checklistNote.class">
                            {{ checklistNote.text }}
                        </div>
                    </div>

                    <div class="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                        <div class="border-b border-gray-300 px-6 py-3">
                            <h3 class="text-lg font-bold text-gray-700">Account</h3>
                        </div>
                        <div class="space-y-4 px-6 py-5">
                            <Field label="Onboarding">
                                <span class="flex items-center gap-1.5">
                                    <UIcon :name="isInvited ? 'i-ph-envelope-simple-duotone' : 'i-ph-user-gear-duotone'" class="size-4 text-gray-400" />
                                    {{ isInvited ? 'Invited by email' : 'Built by admin' }}
                                </span>
                            </Field>
                            <Field label="Login email" truncate>
                                <span>{{ profile.email }}</span>
                            </Field>
                            <Field label="Access email sent">
                                <template v-if="profile.invited_at">
                                    <span class="block">{{ dayjs(profile.invited_at).format('lll') }}</span>
                                    <span class="block text-xs text-gray-400">{{ dayjs(profile.invited_at).fromNow() }}</span>
                                </template>
                            </Field>
                            <Field v-if="profile.status === DoctorStatus.published" label="Published">
                                <span v-if="profile.published_at">{{ dayjs(profile.published_at).format('lll') }}</span>
                            </Field>
                            <Field label="Created">
                                <span>{{ dayjs(profile.created_at).format('lll') }}</span>
                            </Field>
                            <div class="space-y-2 border-t border-dashed border-gray-300 pt-4">
                                <p class="text-xs text-gray-500">The access email holds a 6-digit code. The doctor enters it on the portal sign-in page to set their password.</p>
                                <UButton @click="copyPortalLink(profile.email)" color="white" size="sm" icon="i-ph-link-duotone">
                                    Copy portal sign-in link
                                </UButton>
                            </div>
                        </div>
                    </div>

                    <div class="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5">
                        <div class="border-b border-gray-300 px-6 py-3">
                            <h3 class="text-lg font-bold text-gray-700">Pricing</h3>
                        </div>
                        <dl class="space-y-3 px-6 py-5">
                            <Field label="Scheme" inline>Fixed</Field>
                            <Field label="Session price" inline>
                                <span v-if="Number(profile.session_price_usd) > 0" class="font-medium tabular-nums">{{ usd.format(Number(profile.session_price_usd)) }}</span>
                            </Field>
                            <Field label="Duration" inline>{{ profile.session_duration_min }} min</Field>
                            <Field label="Buffer" inline>{{ profile.buffer_min ? `${profile.buffer_min} min` : 'None' }}</Field>
                        </dl>
                    </div>
                </div>
            </div>

            <TelemedicineSuspendDoctorModal v-model:open="overlays.suspend" :doctor="profile" @reload="load" />
        </template>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { UserRole } from '~/types/user'
import { DoctorOnboardingPath, DoctorStatus, type DoctorProfile, type DoctorProfileFields, type Specialty } from '~/types/telemedicine'
import { PUBLISH_INVARIANT_MESSAGE, canTransition, changedDoctorFields, doctorPhotoUrl, doctorProfileFormSchema, isPublishInvariantError, normalizeDoctorFields, pickDoctorFields, publishBlockers, publishChecklist } from '~/utils/telemedicineDoctor'

dayjs.extend(LocalizedFormat)
dayjs.extend(RelativeTime)

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.telemedicineAdmin] })

const route = useRoute()
const client = useSupabaseClient()
const config = useRuntimeConfig()
const toast = useToast()
const { changeStatus, resendAccessEmail, copyPortalLink } = useDoctorActions()

const id = computed(() => route.params.id as string)
const loading = ref(true)
const saving = ref(false)
const busy = ref<DoctorStatus | 'resend'>()
const profile = ref<DoctorProfile>()
const specialties = ref<Specialty[]>([])
const specialtiesFailed = ref(false)
const state = ref<DoctorProfileFields>()
const form = ref()

const overlays = reactive({ suspend: false })

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

const displayName = computed(() => (profile.value ? [profile.value.title, profile.value.full_name].filter(Boolean).join(' ') : ''))

useHead({ title: computed(() => (profile.value ? `Telemedicine – ${displayName.value}` : 'Telemedicine – Doctor')) })
const photoUrl = computed(() => (profile.value ? doctorPhotoUrl(config.public.supabase.url, profile.value) ?? undefined : undefined))
const isInvited = computed(() => profile.value?.onboarding_path === DoctorOnboardingPath.invite)
const canSuspend = computed(() => !!profile.value && canTransition(profile.value.status, DoctorStatus.suspended))

const normalized = computed(() => (state.value ? normalizeDoctorFields(state.value) : undefined))
const dirty = computed(() => !!profile.value && !!normalized.value
    && JSON.stringify(normalized.value) !== JSON.stringify(normalizeDoctorFields(pickDoctorFields(profile.value))))

const specialtyActive = computed(() => {
    const specialtyId = normalized.value?.specialty_id
    if (!specialtyId) return undefined
    return specialties.value.find((s) => s.id === specialtyId)?.active
        ?? (profile.value?.specialty?.id === specialtyId ? profile.value.specialty.active : undefined)
})

const publishInput = computed(() => (profile.value && normalized.value
    ? { ...normalized.value, user_id: profile.value.user_id, specialty_active: specialtyActive.value }
    : undefined))
const liveBlockers = computed(() => (publishInput.value ? publishBlockers(publishInput.value) : []))
const publishReady = computed(() => !dirty.value && liveBlockers.value.length === 0)

const checklist = computed(() => (publishInput.value ? publishChecklist(publishInput.value) : []))
const checklistDone = computed(() => checklist.value.filter((item) => item.ok).length)

const checklistNote = computed(() => {
    if (profile.value?.status === DoctorStatus.published) {
        return specialtyActive.value === false
            ? { text: 'Hidden from the app: the specialty is inactive.', class: 'text-amber-600' }
            : { text: 'Live in the SAMU app.', class: 'text-green-700' }
    }
    if (profile.value?.status === DoctorStatus.suspended) {
        return { text: 'Hidden from the app while suspended.', class: 'text-red-600' }
    }
    if (liveBlockers.value.length) {
        return { text: 'Complete every item to publish.', class: 'text-gray-500' }
    }
    return dirty.value
        ? { text: 'Save your changes, then publish.', class: 'text-gray-700' }
        : { text: 'Ready to publish.', class: 'text-green-700' }
})

onMounted(async () => {
    await Promise.all([load(), fetchSpecialties()])
    loading.value = false
})

async function load() {
    const keepEdits = dirty.value
    const { data, error } = await client.from('doctor_profiles')
        .select('*, specialty:specialties(id, name_en, name_ar, active)')
        .eq('id', id.value)
        .maybeSingle()
    if (error) {
        toast.add({ title: 'Could not load the doctor', description: error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }
    profile.value = (data ?? undefined) as unknown as DoctorProfile | undefined
    if (profile.value && !keepEdits) state.value = pickDoctorFields(profile.value)
}

async function fetchSpecialties() {
    const { data, error } = await client.from('specialties').select('*').order('sort_order').order('name_en')
    specialtiesFailed.value = !!error
    if (error) {
        toast.add({ title: 'Could not load specialties', description: error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }
    specialties.value = (data ?? []) as unknown as Specialty[]
}

function reset() {
    if (profile.value) state.value = pickDoctorFields(profile.value)
    form.value?.clear()
}

async function save() {
    const next = normalized.value
    if (!profile.value || !next) return
    const changes = changedDoctorFields(normalizeDoctorFields(pickDoctorFields(profile.value)), next)
    if (!Object.keys(changes).length) return
    saving.value = true

    const { error } = await client.from('doctor_profiles')
        .update(changes as never)
        .eq('id', profile.value.id)

    saving.value = false

    if (error) {
        toast.add({
            title: 'Could not save the profile',
            description: isPublishInvariantError(error) ? PUBLISH_INVARIANT_MESSAGE : error.message,
            icon: 'i-ph-warning-duotone',
            color: 'red',
        })
        return
    }

    toast.add({ title: 'Profile saved', icon: 'i-ph-check-circle-duotone', color: 'green' })
    state.value = { ...next }
    await load()
    reset()
}

async function setStatus(next: DoctorStatus) {
    if (!profile.value) return
    busy.value = next
    if (await changeStatus(profile.value, next)) await load()
    busy.value = undefined
}

async function resend() {
    if (!profile.value) return
    busy.value = 'resend'
    if (await resendAccessEmail(profile.value)) await load()
    busy.value = undefined
}
</script>
